// Limited-time offers: buy one product, get others free. Each offer runs
// between its dates (UTC) and switches itself on and off; nothing needs a
// deploy at either end.
//
// At checkout (api/screensaver/checkout.js, api/wallpaper/checkout.js,
// api/xr/checkout.js) an offer that covers the product puts its free products
// on the session (metadata.bonus, metadata.offer) and on the success URL
// (&bonus=). api/_lib/orders.js turns them into order rows of their own
// (<session id>:<product>, amount 0), so the free products are owned exactly
// like bought ones, and a refund of the purchase takes them back. The GET of
// each checkout reports the offer, so the static pages show it only while it
// runs.

export const OFFERS = [
  // The live wallpapers' launch: buy them, get the screensaver free. One way
  // only: both ways would make the $1.99 screensaver the price of both.
  {
    id: 'desktop-bogo-2026-10',
    start: '2026-10-13T00:00:00Z',
    end: '2026-11-13T00:00:00Z',
    free: { wallpaper: ['screensaver-mac'] },
  },
  // Then VR and AR mode with both desktop products free.
  {
    id: 'vr-desktop-2026-11',
    start: '2026-11-13T00:00:00Z',
    end: '2026-12-13T00:00:00Z',
    free: { xr: ['screensaver-mac', 'wallpaper'] },
  },
];

// The offer running now for a product: { id, free, end } or null.
export function activeOffer(product, now = new Date()) {
  const t = now.getTime();
  const o = OFFERS.find((x) => x.free[product] && Date.parse(x.start) <= t && t < Date.parse(x.end));
  return o ? { id: o.id, free: o.free[product], end: o.end } : null;
}

// The offer for a product, limited to the free products that can be
// delivered right now (`deliverable(product)` -> bool), or null.
export async function offerFor(product, deliverable, now = new Date()) {
  const o = activeOffer(product, now);
  if (!o) return null;
  const ok = await Promise.all(o.free.map((p) => Promise.resolve(deliverable(p)).catch(() => false)));
  const free = o.free.filter((p, i) => ok[i]);
  return free.length ? { ...o, free } : null;
}

// What a checkout session needs for an offer: metadata and the success
// URL's query.
export function offerSession(offer) {
  if (!offer) return { metadata: {}, query: '' };
  return { metadata: { offer: offer.id, bonus: offer.free.join(',') }, query: `&bonus=${encodeURIComponent(offer.free.join(','))}` };
}

// Whether each product can be handed over now. Lazy imports keep each
// checkout's bundle to what it uses.
export async function canDeliver(product) {
  if (product === 'wallpaper') {
    const { currentRelease } = await import('../wallpaper/_releases.js');
    return Boolean(await currentRelease('mac').catch(() => null));
  }
  if (product === 'screensaver-mac') {
    const { currentReleases } = await import('../screensaver/_releases.js');
    return Object.keys(await currentReleases().catch(() => ({}))).length > 0;
  }
  if (product === 'xr' || product === 'qmax') return Boolean(process.env.STRIPE_SECRET_KEY && process.env.QMAX_LICENSE_SECRET);
  return false;
}
